import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Group, Skeleton, Stack, Text } from '@repletoryreach/sdk-ui';
import { Alert, EmptyState, PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifyError, notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, type admin } from '@repletoryreach/sdk-api';
import { useAdminLanguages, useLanguageWordings, useTranslateWordings } from '../../../../data/useLanguages';
import { usePersistedState } from '../../../../lib/usePersistedState';
import { formatInteger } from '../../../../lib/format';
import { useT } from '../../../../i18n/I18nProvider';
import { linesInCode } from '../languages';
import { WordingRow } from '../components/WordingRow';
import '../languages.css';

type Filter = 'all' | 'missing' | 'unread' | 'stale';
const FILTERS: readonly Filter[] = ['all', 'missing', 'unread', 'stale'];

const matches = (f: Filter, w: admin.LanguageWording): boolean =>
  f === 'missing' ? w.source === 'missing'
    : f === 'unread' ? w.source !== 'missing' && !w.reviewed
      : f === 'stale' ? w.stale
        : true;

/**
 * One language's built-in lines (1 November 2026): the chatbot's own fixed wording (the language menu, "Switch to
 * ...?", hand-over notices, buttons) with the English beside each translation. Lines are machine-translated once at
 * the platform's cost; here the owner translates the missing ones now, corrects a line in place and marks it as read
 * by a native speaker. English and Afrikaans are written in the code and shown only.
 */
export default function LanguageLinesPage() {
  const t = useT();
  const navigate = useNavigate();
  const { code = '' } = useParams();
  const languagesQ = useAdminLanguages();
  const wordingsQ = useLanguageWordings(code);
  const translate = useTranslateWordings(code);
  const [filter, setFilter] = usePersistedState<Filter>('admin.languages.lines.filter', 'all');

  const language = languagesQ.data?.data.find((l) => l.code === code);
  const name = language?.native_name ?? code;
  const inCode = linesInCode(code);
  const lines = useMemo(() => wordingsQ.data?.data ?? [], [wordingsQ.data]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f, lines.filter((w) => matches(f, w)).length])) as Record<Filter, number>, [lines]);
  const shown = useMemo(() => lines.filter((w) => matches(filter, w)), [lines, filter]);
  const notFound = isApiError(wordingsQ.error) && wordingsQ.error.status === 404;
  const denied = isApiError(wordingsQ.error) && wordingsQ.error.status === 403;

  function run(all: boolean) {
    translate.mutate(all, {
      onSuccess: (res) => notifySuccess(t('billing.languages.lines.translated', { count: formatInteger(res.data.written), name }), t('billing.languages.lines.titleShort')),
      onError: (e) => notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.languages.lines.translateFailed'))
    });
  }

  function confirmRemake() {
    openConfirmModal({
      title: t('billing.languages.lines.remake.title', { name }),
      children: <Text role="bodySmall">{t('billing.languages.lines.remake.body', { name })}</Text>,
      labels: { confirm: t('billing.languages.lines.remake.confirm'), cancel: t('action.cancel') },
      confirmProps: { color: 'orange' },
      onConfirm: () => run(true)
    });
  }

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('billing.languages.title')}
        title={t('billing.languages.lines.title', { name })}
        description={t('billing.languages.lines.description', { name: language?.name ?? code })}
        actions={
          <Group gap="sm" wrap="wrap">
            <Button size="sm" variant="subtle" onClick={() => navigate('/billing/languages')}>{t('billing.languages.lines.back')}</Button>
            {!inCode && (
              <>
                <Button size="sm" variant="outline" disabled={translate.isPending || lines.length === 0} onClick={confirmRemake}>
                  {t('billing.languages.lines.remake.action')}
                </Button>
                <Button size="sm" loading={translate.isPending} disabled={counts.missing === 0} onClick={() => run(false)} data-testid="translate-missing">
                  {t('billing.languages.lines.translate')}
                </Button>
              </>
            )}
          </Group>
        }
      />

      {inCode && <Alert color="info">{t('billing.languages.lines.inCodeNote')}</Alert>}
      {!inCode && <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.languages.lines.costNote')}</Text>}
      {language?.quality === 'review' && <Alert color="warning">{t('billing.languages.lines.reviewNote', { name })}</Alert>}
      {language && !language.enabled && <Alert color="info">{t('billing.languages.lines.offNote', { name })}</Alert>}

      {wordingsQ.isLoading ? (
        <Stack gap="sm">
          {[0, 1, 2].map((i) => <Skeleton key={i} height={120} />)}
        </Stack>
      ) : wordingsQ.error ? (
        <Alert color="error">
          <Stack gap="sm">
            <Text role="bodySmall">
              {notFound ? t('billing.languages.lines.notFound', { code }) : denied ? t('billing.languages.denied') : t('billing.languages.lines.loadError')}
            </Text>
            {!notFound && !denied && (
              <Group>
                <Button size="xs" variant="outline" onClick={() => void wordingsQ.refetch()}>{t('action.retry')}</Button>
              </Group>
            )}
          </Stack>
        </Alert>
      ) : (
        <Stack gap="md">
          {!inCode && (
            <SegmentedControl
              value={filter}
              onChange={(v) => setFilter(FILTERS.find((f) => f === v) ?? 'all')}
              data={FILTERS.map((f) => ({ value: f, label: t(`billing.languages.lines.filter.${f}`, { count: formatInteger(counts[f]) }) }))}
            />
          )}
          {shown.length === 0 ? (
            <EmptyState
              title={filter === 'all' ? t('billing.languages.lines.empty') : t('billing.languages.lines.emptyFiltered')}
              action={filter !== 'all'
                ? <Button size="xs" variant="outline" onClick={() => setFilter('all')}>{t('billing.languages.lines.showAll')}</Button>
                : undefined}
            />
          ) : (
            <Stack gap="xs">
              {shown.map((w) => <WordingRow key={w.key} code={code} languageName={name} line={w} readOnly={inCode} />)}
            </Stack>
          )}
        </Stack>
      )}
    </Stack>
  );
}
